<script lang="ts">
/**
 * @component GoogleAnalytics
 * @description GDPR-compliant Google Analytics 4 integration.
 *
 * Uses a two-phase loading strategy:
 * 1. **Before consent**: Only injects Consent Mode v2 defaults (all denied).
 *    No external scripts are loaded, no data leaves the browser.
 * 2. **After consent**: Loads gtag.js and flushes any events that were queued
 *    in the meantime by the `ga` module.
 *
 * The component reactively watches `cookieState.tracking` and calls `init()`
 * when consent is granted. If consent is later revoked, it sends a consent
 * update to gtag (but the script stays loaded — unloading is not possible).
 *
 * Supports multiple measurement IDs and per-property configurations.
 *
 * @example
 * ```svelte
 * <!-- Minimal: uses PUBLIC_GOOGLE_ANALYTICS_MEASUREMENT_ID from env -->
 * <GoogleAnalytics />
 *
 * <!-- With extra properties and per-property config -->
 * <GoogleAnalytics
 *   properties={['G-SECONDARY-ID']}
 *   configurations={{ 'G-SECONDARY-ID': { send_page_view: false } }}
 * />
 *
 * <!-- Start disabled, enable later -->
 * <GoogleAnalytics bind:this={ga} enabled={false} />
 * ```
 *
 * @see {@link $lib/analytics} for the typed `ga` helper module
 * @see {@link cookieState} for the reactive consent state
 */
import { onMount } from "svelte";
import { browser } from "$app/env";
import { page } from "$app/state";
import { cookieState } from "$lib/components/cookie/cookieState.svelte";
import { PUBLIC_GOOGLE_ANALYTICS_MEASUREMENT_ID } from "$app/env/public";
import { locale } from "$lib/i18n";
import { ga } from "$lib/analytics";

interface Props {
  /** Additional measurement IDs beyond the env default. */
  properties?: string[];
  /** Per-property configuration overrides (keyed by measurement ID). */
  configurations?: Record<string, Record<string, unknown>>;
  /**
   * Master switch. When `false`, gtag.js is not loaded even if consent
   * is granted. Bind to this to manually control initialization.
   * Defaults to `true`.
   */
  enabled?: boolean;
}

let { properties = [], configurations = {}, enabled = true }: Props = $props();

const allProperties = $derived([
  PUBLIC_GOOGLE_ANALYTICS_MEASUREMENT_ID,
  ...properties,
]);
const measurement_id = PUBLIC_GOOGLE_ANALYTICS_MEASUREMENT_ID;

let initialized = $state(false);

// ---------------------------------------------------------------------------
// Initialization — load gtag.js + flush queue
// ---------------------------------------------------------------------------

/**
 * Load the gtag.js script and configure all measurement IDs.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function init(): void {
  if (initialized || !enabled) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurement_id}`;
  document.head.appendChild(script);

  script.onload = () => {
    const dataLayer = (window.dataLayer = window.dataLayer || []);
    // biome-ignore lint: gtag variadic signature
    const push = (...args: unknown[]) => dataLayer.push(args);
    push("js", new Date());
    for (const p of allProperties) {
      const cfg = configurations[p] || {};
      push("config", p, cfg);
    }
    ga.flush();
  };
}

// ---------------------------------------------------------------------------
// Reactive consent — watch cookieState.tracking
// ---------------------------------------------------------------------------

$effect(() => {
  if (cookieState.tracking && !initialized) {
    init();
  }
});

$effect(() => {
  if (typeof window.gtag === "function") {
    const consentValue = cookieState.tracking ? "granted" : "denied";
    window.gtag?.("consent", "update", {
      analytics_storage: consentValue,
      ad_storage: consentValue,
      ad_user_data: consentValue,
      ad_personalization: consentValue,
    });
  }
});

// ---------------------------------------------------------------------------
// Page tracking — send page info on every route change (only after init)
// ---------------------------------------------------------------------------

$effect(() => {
  if (initialized && typeof window.gtag === "function") {
    window.gtag?.("config", measurement_id, {
      page_title: document.title,
      page_path: page.url.pathname,
      page_location: page.url.href,
      page_referrer: document.referrer,
      search_term: page.url.searchParams.get("q"),
      page_language: locale.toString(),
    });
  }
});

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMount(() => {
  if (cookieState.tracking && enabled) {
    init();
  }
});
</script>

<svelte:head>
  <!--
    Google Analytics Consent Mode v2 Defaults
    This inline script MUST run before gtag.js loads. It tells Google that
    all consent categories are denied by default. Once the user grants
    consent, we send a consent update via gtag('consent', 'update', ...).
  -->
  {@html `<script>
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}

    gtag('consent', 'default', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'wait_for_update': 500
    });
  </script>`}
</svelte:head>
